import { useState } from 'react'
import {
  PaginationPayload,
  SortPayload,
  useDataProvider,
  useRedirect
} from 'react-admin'
import Partner from '../types/Partner'
import { UserRole } from '../../../authProvider'
import Storage from '../../../lib/Storage'

type Filter = {
  id?: string
  q?: string
}

export type SortByOption = 'name' | 'company'
export type PartnerType = 'publishers' | 'affiliates' | 'vendors'
type RoleType = 'PUBLISHER' | 'AFFILIATE' | 'VENDOR'

const DEFAULT_PAGE = 1
const DEFAULT_TOTAL_PAGES = 1
const DEFAULT_PER_PAGE = 24
const DEFAULT_SORT_FIELD = 'name'
const DEFAULT_SORT_ORDER = 'ASC'
const ON_SELECT_ROUTE = '/sales'

const useAdmin = () => {
  const dataProvider = useDataProvider()
  const redirect = useRedirect()

  const [partners, setPartners] = useState<Partner[]>([])
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES)
  const [partnerType, setPartnerType] = useState<PartnerType>('publishers')
  const [filter, setFilter] = useState<Filter>({})

  const [sort, setSort] = useState<SortPayload>({
    field: DEFAULT_SORT_FIELD,
    order: DEFAULT_SORT_ORDER
  })

  const [pagination, setPagination] = useState<PaginationPayload>({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE
  })

  const setPage = (page: number) => setPagination({ ...pagination, page })

  const setFilters = (key: keyof Filter, value: string) => {
    setPage(DEFAULT_PAGE)

    if (value.length > 0) {
      setFilter({ ...filter, [key]: value })
    } else {
      const { [key]: _, ...rest } = filter as Filter
      setFilter(rest)
    }
  }

  const setSortBy = (field: SortByOption) => setSort({ ...sort, field })

  const setPartnerTypeAndResetParams = (type: PartnerType) => {
    setPartnerType(type)
    setPage(DEFAULT_PAGE)
    setSortBy(DEFAULT_SORT_FIELD)
  }

  const selectPartner = (partner: Partner) => {
    const subName = partner.company || partner.hostname || ''
    const type = partnerType.toUpperCase().slice(0, -1) as RoleType
    const role: UserRole = {
      type,
      subname: subName,
      id: partner.id,
      name: partner.name,
      organizationId: partner.organization.databaseId
    }

    Storage().setActiveRole(role)
    redirect(ON_SELECT_ROUTE)
  }

  const fetchPartners = async () => {
    const { data, total } = await dataProvider.getList(partnerType, {
      pagination,
      sort,
      filter
    })
    const totalPages = Math.ceil((total || 1) / pagination.perPage)

    setPartners(data)
    setTotalPages(totalPages)
  }

  return {
    partners,
    totalPages,
    partnerType,
    pagination,
    filter,
    sort,
    setPage,
    setFilters,
    setPartnerType: setPartnerTypeAndResetParams,
    setSortBy,
    fetchPartners,
    selectPartner
  }
}

export default useAdmin
